/**
 * Immutable dot-path helpers.
 */

const propToArray = (prop) =>
    prop.split('.').reduce((result, element, index, list) => {
        const previous = index > 0 && list[index - 1];

        if (previous && /(?:^|[^\\])\\$/.test(previous)) {
            const current = result.pop();
            result.push(current.slice(0, -1) + '.' + element);
        } else {
            result.push(element);
        }

        return result;
    }, []);

const normalizeProp = (prop) =>
    typeof prop === 'number'
        ? propToArray(prop.toString())
        : typeof prop === 'string'
          ? propToArray(prop)
          : prop;

const getArrayIndex = (head, object) => {
    if (head === '$end') {
        head = Math.max(object.length - 1, 0);
    }

    if (!/^\+?\d+$/.test(head)) {
        throw new Error(`Array index '${head}' has to be an integer`);
    }

    return parseInt(head, 10);
};

export function set(object, prop, value) {
    prop = normalizeProp(prop);

    const setPropImmutableRec = (currentObject, currentProp, currentValue, index) => {
        let clone;
        let head = currentProp[index];

        if (currentProp.length > index) {
            if (Array.isArray(currentObject)) {
                head = getArrayIndex(head, currentObject);
                clone = currentObject.slice();
            } else {
                clone = Object.assign({}, currentObject);
            }

            clone[head] = setPropImmutableRec(
                currentObject[head] !== undefined ? currentObject[head] : {},
                currentProp,
                currentValue,
                index + 1,
            );

            return clone;
        }

        return typeof currentValue === 'function' ? currentValue(currentObject) : currentValue;
    };

    return setPropImmutableRec(object, prop, value, 0);
}

export function get(object, prop, value) {
    prop = normalizeProp(prop);

    for (let i = 0; i < prop.length; i++) {
        if (object === null || typeof object !== 'object') {
            return value;
        }

        let head = prop[i];
        if (Array.isArray(object) && head === '$end') {
            head = object.length - 1;
        }

        object = object[head];
    }

    if (typeof object === 'undefined') {
        return value;
    }

    return object;
}

function deletePath(object, prop) {
    prop = normalizeProp(prop);

    const deletePropImmutableRec = (currentObject, currentProp, index) => {
        let clone;
        let head = currentProp[index];

        if (
            currentObject === null ||
            typeof currentObject !== 'object' ||
            (!Array.isArray(currentObject) &&
                !Object.prototype.hasOwnProperty.call(currentObject, head))
        ) {
            return currentObject;
        }

        if (currentProp.length - 1 > index) {
            if (Array.isArray(currentObject)) {
                head = getArrayIndex(head, currentObject);
                clone = currentObject.slice();
            } else {
                clone = Object.assign({}, currentObject);
            }

            clone[head] = deletePropImmutableRec(currentObject[head], currentProp, index + 1);
            return clone;
        }

        if (Array.isArray(currentObject)) {
            head = getArrayIndex(head, currentObject);
            clone = [].concat(currentObject.slice(0, head), currentObject.slice(head + 1));
        } else {
            clone = Object.assign({}, currentObject);
            delete clone[head];
        }

        return clone;
    };

    return deletePropImmutableRec(object, prop, 0);
}

export { deletePath as delete };

export function toggle(object, prop) {
    const currentValue = get(object, prop);
    return set(object, prop, !Boolean(currentValue));
}

export function merge(object, prop, value) {
    const currentValue = get(object, prop);

    if (typeof currentValue === 'object') {
        if (Array.isArray(currentValue)) {
            return set(object, prop, currentValue.concat(value));
        }

        if (currentValue === null) {
            return set(object, prop, value);
        }

        return set(object, prop, Object.assign({}, currentValue, value));
    }

    if (typeof currentValue === 'undefined') {
        return set(object, prop, value);
    }

    return object;
}

export function findPathByAttr(object, attr, targetId) {
    const helper = (currentObject, currentTargetId, currentPath) => {
        if (typeof currentObject !== 'object' || currentObject === null) {
            return null;
        }

        if (Array.isArray(currentObject)) {
            for (let i = 0; i < currentObject.length; i++) {
                const newPath = currentPath ? `${currentPath}.${i}` : `${i}`;

                if (currentObject[i] && currentObject[i][attr] === currentTargetId) {
                    return newPath;
                }

                const result = helper(currentObject[i], currentTargetId, newPath);
                if (result) {
                    return result;
                }
            }
        } else {
            for (const key in currentObject) {
                if (!Object.prototype.hasOwnProperty.call(currentObject, key)) {
                    continue;
                }

                const newPath = currentPath ? `${currentPath}.${key}` : key;

                if (currentObject[key] && currentObject[key][attr] === currentTargetId) {
                    return newPath;
                }

                const result = helper(currentObject[key], currentTargetId, newPath);
                if (result) {
                    return result;
                }
            }
        }

        return null;
    };

    return helper(object, targetId, '');
}
